import { cleanupUserPrompt } from './cleanupPromptSpelling';
import type { WorkEpicRow } from './networkingTasks';

export type CreatePack = {
  seed: string;
  value: string;
  outcome: string;
  priority: string;
};

export type ParsedCreateDump = CreatePack & {
  score: number | null;
  scoreWhy: string;
  wantsStandalone: boolean;
  epicHint: string;
  forceCreate: boolean;
  clarifyNote: string;
  /** True when the dump covers more than a one-line seed. */
  isRich: boolean;
};

export type SimilarOpenTask = {
  id: string;
  title: string | null;
  notes: string | null;
  status: string;
  strategy_score: number | null;
  epic_id: string | null;
};

const EMPTY_PACK: CreatePack = { seed: '', value: '', outcome: '', priority: '' };

export function emptyCreatePack(): CreatePack {
  return { ...EMPTY_PACK };
}

function extractLabeledBlock(text: string, labels: string[]): string | null {
  const joined = labels.map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const re = new RegExp(
    `(?:^|\\n)\\s*(?:${joined})\\s*[:\\-–—]\\s*([\\s\\S]+?)(?=\\n\\s*(?:value|justify|why|done|outcome|priority|if we|epic|score|standalone)\\s*[:\\-–—]|$)`,
    'i',
  );
  const m = text.match(re);
  const v = m?.[1]?.trim();
  return v && v.length >= 2 ? v.slice(0, 2000) : null;
}

function looksRich(text: string): boolean {
  const t = text.trim();
  if (t.length >= 90) return true;
  if ((t.match(/\n/g) || []).length >= 1) return true;
  if (/\b(so that|this will|in order to|because|so we can|value|justify|done looks|priority|if we don|score\s*[:=]?\s*[1-5]|epic\s*:)\b/i.test(t)) {
    return true;
  }
  // Multi-clause prose (two sentences or comma + purpose)
  if (/[.!?].+\w/.test(t) && t.length >= 60) return true;
  return false;
}

/**
 * Infer planning fields from natural prose (no labels required).
 * Example: "Let's give Jesse access to Supabase so that he can update the card. This will help Kitomer fix issues."
 */
export function inferCreatePackFromProse(raw: string): Partial<CreatePack> & {
  epicHint?: string;
  score?: number | null;
} {
  const text = cleanupUserPrompt(raw).trim();
  if (text.length < 40) return {};

  let seed = '';
  let value = '';
  let outcome = '';
  let priority = '';
  let epicHint = '';

  // "X so that Y. This will Z."
  const soThat = text.match(
    /^(.+?)\s+so that\s+(.+?)(?:\.\s*This will\s+(.+?)(?:\.|$)|(?:\.|$))/i,
  );
  const thisWillOnly = text.match(/\bThis will\s+(.+?)(?:\.|$)/i);

  if (soThat) {
    seed = soThat[1]
      .replace(/^let'?s\s+/i, '')
      .replace(/^i (need to|want to|should|have to)\s+/i, '')
      .replace(/^we (should|need to|want to|must|have to)\s+/i, '')
      .replace(/^give\s+/i, 'Grant ')
      .trim()
      .slice(0, 72);
    const capability = soThat[2].trim().replace(/\.$/, '');
    value = (soThat[3] || thisWillOnly?.[1] || capability).trim().replace(/\.$/, '').slice(0, 160);
    outcome = capability.slice(0, 140);
    priority = `Blocked on related ops if not done.`.slice(0, 140);
  } else if (thisWillOnly) {
    const before = text.slice(0, text.toLowerCase().indexOf('this will')).trim().replace(/\.$/, '');
    seed = before
      .replace(/^let'?s\s+/i, '')
      .replace(/^i (need to|want to|should|have to)\s+/i, '')
      .replace(/^we (should|need to|want to|must|have to)\s+/i, '')
      .replace(/^give\s+/i, 'Grant ')
      .trim()
      .slice(0, 72) || text.slice(0, 42);
    value = thisWillOnly[1].trim().replace(/\.$/, '').slice(0, 160);
    outcome = `Complete: ${seed}`.slice(0, 140);
    priority = `Miss “${value.slice(0, 80)}” if not done.`.slice(0, 140);
  } else if (text.length >= 90) {
    // Single block — first sentence work, rest value-ish
    const sentences = text.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
    seed = (sentences[0] || text)
      .replace(/^let'?s\s+/i, '')
      .replace(/^i (need to|want to|should|have to)\s+/i, '')
      .replace(/^we (should|need to|want to|must|have to)\s+/i, '')
      .replace(/^give\s+/i, 'Grant ')
      .trim()
      .slice(0, 72);
    value = (sentences.slice(1).join(' ').trim() || `Unblocks: ${seed}`).slice(0, 160);
    outcome = `Verified complete.`.slice(0, 140);
    priority = `Stays blocked if not done.`.slice(0, 140);
  }

  // Epic / product hints from prose
  if (/\b(kitomer|supabase|microconf|ops)\b/i.test(text)) {
    const m = text.match(/\b(kitomer|supabase|microconf)\b/i);
    if (m) epicHint = m[1];
  }

  // Tighten seed to a card title when long
  if (seed.length > 100) {
    seed = seed.slice(0, 97).replace(/\s+\S*$/, '') + '…';
  }

  return {
    seed: seed.slice(0, 4000),
    value: value.slice(0, 2000),
    outcome: outcome.slice(0, 2000),
    priority: priority.slice(0, 2000),
    epicHint: epicHint || undefined,
    score: null,
  };
}

/** Parse a planning dump — labeled sections and/or a solid multi-line prompt. */
export function parseCreateDump(raw: string): ParsedCreateDump {
  const text = cleanupUserPrompt(raw).trim();
  let value =
    extractLabeledBlock(text, ['value', 'justify', 'why it matters', 'why', 'business value']) ?? '';
  let outcome =
    extractLabeledBlock(text, ['done looks like', 'done', 'outcome', 'definition of done']) ?? '';
  let priority =
    extractLabeledBlock(text, [
      'priority',
      "if we don't",
      'if we dont',
      'if not done',
      'cost of delay',
    ]) ?? '';
  let epicHint = extractLabeledBlock(text, ['epic', 'plan', 'on epic']) ?? '';

  let score: number | null = null;
  let scoreWhy = '';
  const scoreMatch =
    text.match(/\bscore\s*[:=]?\s*([1-5])\b/i) ||
    text.match(/\b([1-5])\s*\/\s*5\b/) ||
    text.match(/(?:^|\n)\s*([1-5])(?:\s*[-:·]|$)/);
  if (scoreMatch) {
    score = Number(scoreMatch[1]);
    const after = text.slice(text.indexOf(scoreMatch[0]) + scoreMatch[0].length).trim();
    const whyLine = after.split('\n')[0]?.replace(/^[\s:·\-–—]+/, '').trim() ?? '';
    if (whyLine.length > 8 && !/^(value|done|priority|epic)\b/i.test(whyLine)) {
      scoreWhy = whyLine.slice(0, 500);
    }
  }

  const wantsStandalone =
    /\b(standalone|ops[-\s]?inbox|inbox only)\b/i.test(text) &&
    !/\b(not standalone|on epic)\b/i.test(text);

  const forceCreate =
    /\b(force( create)?|create anyway|not a duplicate|different task|still create)\b/i.test(text);
  const clarifyMatch = text.match(/\bclarify\s*[:\-]\s*([\s\S]+?)(?=\n\n|$)/i);
  const clarifyNote = clarifyMatch?.[1]?.trim().slice(0, 1000) ?? '';

  // Seed = first line / unlabeled body without stripping labeled sections entirely
  const lines = text.split('\n').map((l) => l.trim());
  const seedLines: string[] = [];
  for (const line of lines) {
    if (
      /^(value|justify|why|done|outcome|priority|if we|epic|score|standalone|force|clarify)\b/i.test(
        line,
      )
    ) {
      break;
    }
    if (line) seedLines.push(line);
  }
  let seed = seedLines.join('\n').trim() || text.split('\n')[0]?.trim() || text;
  seed = seed.slice(0, 4000);

  const isRich = looksRich(text);

  // Natural prose without labels: keep full dump as seed; OpenAI fills the rest.
  if (isRich && !value && !outcome && !priority) {
    seed = text.slice(0, 4000);
  }

  return {
    seed,
    value,
    outcome,
    priority,
    score,
    scoreWhy,
    wantsStandalone,
    epicHint,
    forceCreate,
    clarifyNote,
    isRich,
  };
}

export function mergeCreatePack(prev: CreatePack, parsed: ParsedCreateDump): CreatePack {
  return {
    seed: parsed.seed.trim() || prev.seed,
    value: parsed.value.trim() || prev.value,
    outcome: parsed.outcome.trim() || prev.outcome,
    priority: parsed.priority.trim() || prev.priority,
  };
}

export function missingCreateFields(pack: CreatePack): Array<keyof CreatePack> {
  const missing: Array<keyof CreatePack> = [];
  if (!pack.seed.trim() || pack.seed.trim().length < 3) missing.push('seed');
  if (!pack.value.trim()) missing.push('value');
  if (!pack.outcome.trim()) missing.push('outcome');
  if (!pack.priority.trim()) missing.push('priority');
  return missing;
}

export function nextMissingCreateStep(
  pack: CreatePack,
): 'seed' | 'value' | 'outcome' | 'priority' | 'placement' {
  const m = missingCreateFields(pack);
  if (m.includes('seed')) return 'seed';
  if (m.includes('value')) return 'value';
  if (m.includes('outcome')) return 'outcome';
  if (m.includes('priority')) return 'priority';
  return 'placement';
}

export function askPromptForStep(step: 'seed' | 'value' | 'outcome' | 'priority'): string {
  switch (step) {
    case 'seed':
      return 'What’s the work? (Or paste a full dump — value / done / priority labeled — and I’ll only ask what’s missing.)';
    case 'value':
      return 'Still need value: what does this pull — money, goals, network, or relationship? Be concrete.';
    case 'outcome':
      return 'Still need done: what does done look like — one concrete outcome we can check off?';
    case 'priority':
      return 'Still need priority: what happens if we don’t get this done?';
  }
}

export function formatGotSummary(pack: CreatePack, extras?: string[]): string {
  const got: string[] = [];
  if (pack.seed.trim()) got.push('work');
  if (pack.value.trim()) got.push('value');
  if (pack.outcome.trim()) got.push('done');
  if (pack.priority.trim()) got.push('priority');
  const lines: string[] = [];
  if (got.length) {
    lines.push('Captured');
    lines.push(got.map((g) => `· ${g}`).join('\n'));
  } else {
    lines.push('Looking for a solid work dump.');
  }
  const extraBlocks = (extras ?? []).map((e) => e.trim()).filter(Boolean);
  if (extraBlocks.length) {
    lines.push('');
    lines.push(...extraBlocks);
  }
  return lines.join('\n');
}

const SIMILAR_STOPWORDS = new Set([
  'that',
  'this',
  'with',
  'from',
  'have',
  'need',
  'able',
  'will',
  'give',
  'more',
  'than',
  'then',
  'into',
  'onto',
  'about',
  'after',
  'before',
  'update',
  'access',
  'account',
  'ability',
  'issues',
  'issue',
  'email',
  'task',
  'work',
  'show',
  'related',
  'server',
  'stack',
  'style',
  'state',
  'panel',
  'pick',
]);

/** Token overlap score for open-task near-duplicates. */
export function scoreSimilarTask(
  corpus: string,
  task: { title: string | null; notes: string | null },
): number {
  const tokens = new Set(
    corpus
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length >= 5 && !SIMILAR_STOPWORDS.has(w)),
  );
  if (tokens.size === 0) return 0;
  const title = (task.title ?? '').toLowerCase();
  const hay = `${title} ${task.notes ?? ''}`.toLowerCase();
  let hits = 0;
  let distinctive = 0;
  for (const t of tokens) {
    if (!hay.includes(t)) continue;
    hits += title.includes(t) ? 3 : 1;
    if (t.length >= 7) distinctive += 1;
  }
  if (title && corpus.toLowerCase().includes(title) && title.length >= 10) hits += 8;
  // Require at least one distinctive token (proper-ish / longer) unless title contained
  if (distinctive === 0 && hits < 8) return Math.min(hits, 2);
  return hits;
}

export function pickSimilarTasks(
  corpus: string,
  tasks: SimilarOpenTask[],
  limit = 3,
): SimilarOpenTask[] {
  return tasks
    .map((t) => ({ t, s: scoreSimilarTask(corpus, t) }))
    .filter((x) => x.s >= 6)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.t);
}

export function formatSimilarTasksPushback(tasks: SimilarOpenTask[]): string {
  if (tasks.length === 0) return '';
  const lines = tasks.map((t, i) => {
    const title = (t.title?.trim() || 'Untitled').slice(0, 72);
    const score = t.strategy_score != null ? ` (score ${t.strategy_score})` : '';
    return `${i + 1}. ${title}${score}`;
  });
  return [
    'Possible duplicates (open tasks)',
    ...lines,
    '',
    'Is this different work?',
    'Reply: clarify: <how it’s different>',
    'Or: force create',
  ].join('\n');
}

export function acceptsForceCreate(text: string): boolean {
  return /\b(force( create)?|create anyway|not a duplicate|different task|still create|proceed)\b/i.test(
    text.trim(),
  );
}

export function extractClarifyNote(text: string): string | null {
  const m = text.match(/\bclarify\s*[:\-]\s*([\s\S]+)/i);
  const v = m?.[1]?.trim();
  if (v && v.length >= 8) return v.slice(0, 1000);
  // Free-form differentiation if long enough and not just "yes"
  const t = text.trim();
  if (t.length >= 40 && !acceptsForceCreate(t) && !/^(y|yes|ok|okay)$/i.test(t)) {
    return t.slice(0, 1000);
  }
  return null;
}

/** Prefer recommending against ops-inbox when epicHint or corpus matches. */
export function resolveEpicFromHint(
  hint: string,
  epics: WorkEpicRow[],
): WorkEpicRow | null {
  const t = hint.trim().toLowerCase();
  if (!t) return null;
  const list = epics.filter((e) => e.status === 'active' || e.status === 'paused');
  return (
    list.find(
      (e) =>
        t === e.slug.toLowerCase() ||
        t === e.title.toLowerCase() ||
        e.title.toLowerCase().includes(t) ||
        t.includes(e.title.toLowerCase()) ||
        e.slug.toLowerCase().includes(t.replace(/\s+/g, '-')),
    ) ?? null
  );
}
